import React, { useEffect, useState } from 'react';
import { Input, Modal, Tabs, Typography, Button, message, Popconfirm } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';

const { Paragraph, Text, Link } = Typography;

interface TokenModalProps {
  open: boolean;
  onCancel: () => void;
}

const TokenModal: React.FC<TokenModalProps> = ({ open, onCancel }) => {
  const { t } = useTranslation();
  const [tokenInfo, setTokenInfo] = useState<{ token?: string; message?: string; generatedTime?: string }>({});
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const fetchToken = () => {
    setLoading(true);
    fetch('/login/api/token', {
      method: 'GET',
    })
      .then((res) => res.json())
      .then((e) => {
        console.info(e);
        if (e.isSuccess) {
          setTokenInfo({
            token: e.token || '',
            message: e.message,
          });
        } else {
          setTokenInfo({
            message: e.message || t('tokenModal.fetchFailed'),
          });
        }
      })
      .catch((err) => {
        console.error(err);
        setTokenInfo({
          message: t('tokenModal.fetchFailedRetry'),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      // 打开 Modal 时重置状态并获取 Token
      setTokenInfo({});
      fetchToken();
    }
  }, [open]);

  const handleRegenerate = () => {
    setRegenerating(true);
    fetch('/login/api/token/regenerate', {
      method: 'POST',
    })
      .then((res) => res.json())
      .then((e) => {
        if (e.isSuccess) {
          message.success(t('tokenModal.regenerateSuccess'));
          setTokenInfo({
            token: e.token || '',
            message: e.message,
          });
        } else {
          message.error(e.message || t('tokenModal.regenerateFailed'));
        }
      })
      .catch((err) => {
        console.error(err);
        message.error(t('tokenModal.regenerateFailedRetry'));
      })
      .finally(() => {
        setRegenerating(false);
      });
  };

  return (
    <Modal
      title={t('tokenModal.title')}
      open={open}
      onCancel={onCancel}
      onOk={onCancel}
      okText={t('tokenModal.close')}
      cancelText={t('tokenModal.close')}
      width={800}
      confirmLoading={loading}
    >
      {tokenInfo.token ? (
        <div>
          <Paragraph>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('tokenModal.vipOnly')}
            </Text>
          </Paragraph>
          <Paragraph>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>{t('tokenModal.yourToken')}</Text>
              <Popconfirm
                title={t('tokenModal.regenerateTitle')}
                description={
                  <div>
                    <p style={{ marginBottom: '8px' }}>
                      <Text type="danger" strong>{t('tokenModal.regenerateWarning')}</Text>
                    </p>
                    <p>{t('tokenModal.regenerateDescription')}</p>
                    <p style={{ marginTop: '8px' }}>{t('tokenModal.regenerateConfirm')}</p>
                  </div>
                }
                onConfirm={handleRegenerate}
                okText={t('tokenModal.regenerateOk')}
                cancelText={t('tokenModal.regenerateCancel')}
                okButtonProps={{ danger: true }}
              >
                <Button 
                  type="default" 
                  icon={<ReloadOutlined />} 
                  loading={regenerating}
                  size="small"
                >
                  {t('tokenModal.regenerate')}
                </Button>
              </Popconfirm>
            </div>
          </Paragraph>
          <Paragraph>
            <Input.TextArea
              value={tokenInfo.token}
              readOnly
              autoSize={{ minRows: 1, maxRows: 2 }}
              style={{ fontFamily: 'monospace' }}
            />
          </Paragraph>
          
          <Paragraph style={{ marginTop: '16px' }}>
            <Text strong>{t('tokenModal.usageExample')}</Text>
          </Paragraph>
          
          <Tabs
            items={[
              {
                key: 'java',
                label: 'Java',
                children: (
                  <Input.TextArea
                    readOnly
                    value={`import okhttp3.*;
import java.io.File;
import java.io.IOException;

public class UploadWithOkHttp {
    public static void main(String[] args) throws IOException {
        OkHttpClient client = new OkHttpClient();
        File file = new File("path/to/your/image.jpg");
        
        RequestBody requestBody = new MultipartBody.Builder()
            .setType(MultipartBody.FORM)
            .addFormDataPart("file", file.getName(),
                RequestBody.create(file, MediaType.parse("image/jpeg")))
            .addFormDataPart("fileName", "custom_name.jpg")
            .addFormDataPart("uid", "${tokenInfo.token}")
            .build();
        
        Request request = new Request.Builder()
            .url("https://playground.z.wiki/img/api/upload")
            .post(requestBody)
            .build();
        
        try (Response response = client.newCall(request).execute()) {
            System.out.println("Response code: " + response.code());
            System.out.println("Body: " + response.body().string());
        }
    }
}`}
                    autoSize={{ minRows: 20, maxRows: 30 }}
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                ),
              },
              {
                key: 'curl',
                label: 'cURL',
                children: (
                  <div>
                    <Paragraph>
                      <Text strong>{t('upload.title')}:</Text>
                    </Paragraph>
                    <Input.TextArea
                      readOnly
                      value={`curl -X POST "https://playground.z.wiki/img/api/upload" \\
  -F "file=@/path/to/your/image.jpg" \\
  -F "fileName=custom_name.jpg" \\
  -F "uid=${tokenInfo.token}"`}
                      autoSize={{ minRows: 4, maxRows: 6 }}
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                    <Paragraph style={{ marginTop: '16px' }}>
                      <Text strong>{t('file.delete')}:</Text>
                    </Paragraph>
                    <Input.TextArea
                      readOnly
                      value={`curl -X DELETE "https://playground.z.wiki/img/delete?id=\${id}&uid=${tokenInfo.token}"`}
                      autoSize={{ minRows: 2, maxRows: 3 }}
                      style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                  </div>
                ),
              },
              {
                key: 'javascript',
                label: 'JavaScript',
                children: (
                  <Input.TextArea
                    readOnly
                    value={`// 使用 FormData 上传文件
const formData = new FormData();
formData.append('file', fileInput.files[0]);
formData.append('fileName', 'custom_name.jpg');
formData.append('uid', '${tokenInfo.token}');

fetch('https://playground.z.wiki/img/api/upload', {
  method: 'POST',
  body: formData
})
  .then(response => response.json())
  .then(data => {
    console.log('Upload success:', data);
  })
  .catch(error => {
    console.error('Upload error:', error);
  });

// 删除文件
fetch(\`https://playground.z.wiki/img/delete?id=\${id}&uid=${tokenInfo.token}\`, {
  method: 'DELETE'
})
  .then(response => response.json())
  .then(data => {
    console.log('Delete success:', data);
  })
  .catch(error => {
    console.error('Delete error:', error);
  });`}
                    autoSize={{ minRows: 25, maxRows: 35 }}
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                ),
              },
              {
                key: 'python',
                label: 'Python',
                children: (
                  <Input.TextArea
                    readOnly
                    value={`import requests

# 上传文件
url = "https://playground.z.wiki/img/api/upload"
token = "${tokenInfo.token}"

with open("path/to/your/image.png", "rb") as f:
    files = {"file": ("image.png", f, "image/png")}
    data = {
        "fileName": "custom_name.jpg",
        "uid": token
    }
    response = requests.post(url, files=files, data=data)
    print("Response:", response.json())

# 删除文件
delete_url = f"https://playground.z.wiki/img/delete?id={id}&uid={token}"
response = requests.delete(delete_url)
print("Delete response:", response.json())`}
                    autoSize={{ minRows: 18, maxRows: 25 }}
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                ),
              },
            ]}
          />
          
          <Paragraph style={{ marginTop: '16px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('tokenModal.moreInfo')}
              <Link
                href="https://blog.imgbed.cn/index.php/2025/08/30/%e5%9b%be%e5%ba%8a%e5%b0%8f%e9%95%87-api-%e6%96%87%e6%a1%a3/"
                target="_blank"
              >
                {t('tokenModal.apiDocs')}
              </Link>
            </Text>
          </Paragraph>
        </div>
      ) : (
        <div>
          <Paragraph>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('tokenModal.vipOnly')}
            </Text>
          </Paragraph>
          <Paragraph>
            <Text type="warning">{t('tokenModal.noToken')}</Text>
          </Paragraph>
          <Paragraph>
            <Text>{t('tokenModal.contactForToken')}</Text>
          </Paragraph>
        </div>
      )}
    </Modal>
  );
};

export default TokenModal;

